/**
 * Life4Earth V2 — Project Partners API Adapter
 *
 * CRUD operations for project partner affiliations.
 * Endpoint: /api/Projects/{projectGuid}/partners
 */

import { apiFetch } from "./client";
import { normalizeGuid } from "../utils/guid";

// ── Types (mirror C# DTOs, camelCase from ASP.NET serializer) ───

export interface ProjectPartnerResponse {
  id: string;
  projectGuid: string;
  entityGuid: string | null;
  manualName: string | null;
  manualLogoUrl: string | null;
  manualWebsiteUrl: string | null;
  externalProfileUrl: string | null;
  partnerRole: string | null;
  sortOrder: number;
  isActive: boolean;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateProjectPartnerRequest {
  entityGuid?: string;
  manualName?: string;
  manualLogoUrl?: string;
  manualWebsiteUrl?: string;
  externalProfileUrl?: string;
  partnerRole?: string;
  sortOrder?: number;
}

export interface UpdateProjectPartnerRequest {
  entityGuid?: string | null;
  manualName?: string | null;
  manualLogoUrl?: string | null;
  manualWebsiteUrl?: string | null;
  externalProfileUrl?: string | null;
  partnerRole?: string | null;
  sortOrder?: number;
}

// ── Fetch functions ─────────────────────────────────────────────

export async function fetchProjectPartners(
  projectGuid: string
): Promise<ProjectPartnerResponse[]> {
  const pg = normalizeGuid(projectGuid);
  return apiFetch<ProjectPartnerResponse[]>(
    `/api/Projects/${pg}/partners`
  );
}

export async function createProjectPartner(
  projectGuid: string,
  body: CreateProjectPartnerRequest
): Promise<ProjectPartnerResponse> {
  const pg = normalizeGuid(projectGuid);
  return apiFetch<ProjectPartnerResponse>(
    `/api/Projects/${pg}/partners`,
    { method: "POST", body: JSON.stringify(body) }
  );
}

export async function updateProjectPartner(
  projectGuid: string,
  partnerId: string,
  body: UpdateProjectPartnerRequest
): Promise<void> {
  const pg = normalizeGuid(projectGuid);
  const pid = normalizeGuid(partnerId);
  await apiFetch<void>(
    `/api/Projects/${pg}/partners/${pid}`,
    { method: "PUT", body: JSON.stringify(body) }
  );
}

export async function deleteProjectPartner(
  projectGuid: string,
  partnerId: string
): Promise<void> {
  const pg = normalizeGuid(projectGuid);
  const pid = normalizeGuid(partnerId);
  await apiFetch<void>(
    `/api/Projects/${pg}/partners/${pid}`,
    { method: "DELETE" }
  );
}
